// Multi-color brand icon SVGs.
//
// FontAwesome's brand glyphs are monochrome single-path masks, so they
// can't reproduce the actual Slack 4-color hashtag or the Teams gradient.
// These inline SVGs are the official brand marks (simplified to the
// minimum geometry needed for a 16px header icon) and are tree-shakeable
// because they are plain components.
//
// All three accept a `size` prop and otherwise render at 1em so they
// behave like a font glyph next to the FontAwesome ones.

import React from "react";

const baseProps = (size) => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  xmlns: "http://www.w3.org/2000/svg",
  "aria-hidden": "true",
  focusable: "false",
});

// Slack: 4 colored rounded "pill" pairs forming the # mark.
// Colors: green #2EB67D, blue #36C5F0, red #E01E5A, yellow #ECB22E.
export const SlackBrandIcon = ({ size = "1em" }) => (
  <svg {...baseProps(size)}>
    {/* Green (top-left vertical pair) */}
    <path fill="#36C5F0" d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52z" />
    <path fill="#36C5F0" d="M6.313 15.165a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z" />
    {/* Blue */}
    <path fill="#2EB67D" d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834z" />
    <path fill="#2EB67D" d="M8.834 6.313a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z" />
    {/* Red */}
    <path fill="#ECB22E" d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834z" />
    <path fill="#ECB22E" d="M17.688 8.834a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312z" />
    {/* Yellow */}
    <path fill="#E01E5A" d="M15.165 18.956a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52z" />
    <path fill="#E01E5A" d="M15.165 17.688a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
  </svg>
);

// Microsoft Teams: the official rounded-purple-square app icon with the
// white serif-less "T". Geometry and gradient match the brand assets at
// https://www.microsoft.com/design/brand/ — the avatar bubble that appears
// in some marketing renders is intentionally omitted because it makes the
// mark unreadable below ~24px.
export const TeamsBrandIcon = ({ size = "1em" }) => (
  <svg {...baseProps(size)}>
    <defs>
      <linearGradient id="teamsGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#7B83EB" />
        <stop offset="100%" stopColor="#4B53BC" />
      </linearGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="3.5" fill="url(#teamsGrad)" />
    {/* White "T": horizontal bar + vertical stem */}
    <rect x="5.5" y="7" width="13" height="2.6" rx="0.3" fill="#FFFFFF" />
    <rect x="10.7" y="7" width="2.6" height="10" rx="0.3" fill="#FFFFFF" />
  </svg>
);

// Telegram: the round paper-plane mark with the genuine sky-blue gradient.
export const TelegramBrandIcon = ({ size = "1em" }) => (
  <svg {...baseProps(size)}>
    <defs>
      <linearGradient id="telegramGrad" x1="0.5" y1="0" x2="0.5" y2="1">
        <stop offset="0%" stopColor="#2AABEE" />
        <stop offset="100%" stopColor="#229ED9" />
      </linearGradient>
    </defs>
    <circle cx="12" cy="12" r="12" fill="url(#telegramGrad)" />
    <path
      fill="#FFFFFF"
      d="M5.5 11.7c3.5-1.52 5.83-2.53 7-3.02 3.33-1.39 4.02-1.63 4.47-1.64.1 0 .32.02.46.14.12.1.16.24.18.34.02.1.04.32.02.5-.2 2.05-1.04 7.04-1.47 9.34-.18.97-.54 1.3-.88 1.33-.74.07-1.31-.49-2.03-.96-1.13-.74-1.77-1.2-2.87-1.92-1.27-.83-.45-1.29.28-2.04.19-.2 3.49-3.2 3.55-3.47.01-.04.02-.16-.06-.22-.08-.06-.2-.04-.28-.02-.12.03-2.04 1.3-5.74 3.8-.54.37-1.04.55-1.48.54-.49-.01-1.42-.27-2.12-.5-.86-.28-1.54-.43-1.48-.91.03-.25.38-.5 1.05-.77z"
    />
  </svg>
);
